import React, { useMemo } from "react";
import StatsCard from "../../common/New/StatsCard";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useProgressiveOverviewData } from "../../manageData/Analytics/useProgressiveOverviewData";
import { buildOverviewMetrics } from "./overviewMetrics";
import "./Overview.css";

const CARD_ICONS = {
  trips: MenuBookOutlinedIcon,
  ontime: AccessTimeOutlinedIcon,
  speed: DirectionsCarFilledOutlinedIcon,
  overspeed: ErrorOutlineOutlinedIcon,
  distance: AltRouteOutlinedIcon,
  utilization: BoltOutlinedIcon,
};

function Panel({ title, info, action, onAction, children, className = "" }) {
  const { t } = useTranslation();
  return (
    <section className={`overview-panel ${className}`}>
      <div className="overview-panel-heading">
        <h2>
          {t(title)}{" "}
          {info && <InfoOutlinedIcon titleAccess={t("More information")} />}
        </h2>
        {action && (
          <button
            type="button"
            className="overview-panel-action"
            onClick={onAction}
          >
            {t(action)}
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function LegendRow({ items }) {
  const { t } = useTranslation();
  return (
    <div className="overview-legend">
      {items.map(({ name, color }) => (
        <span key={name}>
          <i style={{ background: color }} />
          {t(name)}
        </span>
      ))}
    </div>
  );
}

export default function Overview({
  selectedDateRange = "today",
  customDateRange = null,
  vehicles = [],
  onViewAll,
}) {
  const { t } = useTranslation();
  const { chartDataStates, error } = useProgressiveOverviewData(
    selectedDateRange,
    customDateRange,
  );

  const isCardLoading = (dataKey) => {
    const state = chartDataStates[dataKey];
    // Show the skeleton only until this card's own stream produces its first
    // result. Later chunks update the value without hiding it again.
    if (!state) return true;
    return state.loading === true && !state.data;
  };

  const metrics = useMemo(
    () =>
      buildOverviewMetrics({
        chartDataStates,
        vehicles,
        selectedDateRange,
        customDateRange,
      }),
    [chartDataStates, vehicles, selectedDateRange, customDateRange],
  );

  const visibleRoutes = metrics.routes.slice(0, 6);
  const maxRouteTrips = Math.max(
    ...visibleRoutes.map((route) => route.trips),
    1,
  );
  const activityMax = Math.max(
    ...metrics.activity.flatMap((row) => [row.trips, row.running, row.alerts]),
    1,
  );
  const hourlyMax = Math.max(...metrics.hourly.map((row) => row.trips), 1);

  return (
    <div className="reports-overview">
      {error && <div className="overview-error">{error}</div>}

      <div className="overview-stats">
        {metrics.cards.map((card) => (
          <StatsCard
            key={card.title}
            title={t(card.title)}
            value={card.value}
            icon={CARD_ICONS[card.iconKey]}
            tone={card.tone}
            loading={isCardLoading(card.dataKey)}
          />
        ))}
      </div>

      <div className="overview-main-grid">
        <Panel
          title="Fleet Activity Timeline"
          info
          className="overview-activity"
        >
          <LegendRow
            items={[
              { name: "Trips", color: "#2f6bea" },
              { name: "Running Vehicles", color: "#22c55e" },
              { name: "Alerts", color: "#ef4444" },
            ]}
          />
          <div className="overview-chart-tall">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={metrics.activity}
                margin={{ top: 12, right: 12, bottom: 0, left: -18 }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="#eef1f6"
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10 }}
                  interval={3}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, Math.ceil(activityMax * 1.2) || 1]}
                  tick={{ fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip />
                <Line
                  type="linear"
                  dataKey="trips"
                  name={t("Trips")}
                  stroke="#2f6bea"
                  strokeWidth={2.5}
                  dot={false}
                />
                <Line
                  type="linear"
                  dataKey="running"
                  name={t("Running Vehicles")}
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="linear"
                  dataKey="alerts"
                  name={t("Alerts")}
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Fleet Health" className="overview-health">
          <div className="overview-health-top">
            <div className="overview-donut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={
                      metrics.health.filter((item) => item.count > 0).length
                        ? metrics.health
                        : [{ name: "Empty", value: 1, color: "#e5e7eb" }]
                    }
                    dataKey="value"
                    innerRadius="72%"
                    outerRadius="94%"
                    startAngle={90}
                    endAngle={-270}
                    stroke="none"
                  >
                    {(metrics.health.some((item) => item.count > 0)
                      ? metrics.health
                      : [{ name: "Empty", color: "#e5e7eb" }]
                    ).map((item) => (
                      <Cell key={item.name} fill={item.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="overview-donut-center">
                <strong>{metrics.healthyPercent}%</strong>
                <small>{t("Healthy")}</small>
              </div>
            </div>
            <div className="overview-breakdown">
              {metrics.health.map((item) => (
                <div key={item.name}>
                  <span>
                    <i style={{ background: item.color }} />
                    {t(item.name)}
                  </span>
                  <span>
                    {item.value}% ({item.count})
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="overview-divider" />
          <div className="overview-panel-heading overview-incidents-heading">
            <h3>{t("Critical Events Summary")}</h3>
            <button
              type="button"
              className="overview-panel-action"
              onClick={onViewAll}
            >
              {t("View all")}
            </button>
          </div>
          <div className="overview-incidents">
            {metrics.incidents.map(([name, count]) => (
              <div key={name}>
                <span>
                  <i />
                  {t(name)}
                </span>
                <strong>{count}</strong>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="overview-bottom-grid">
        <Panel title="Trips by Route" action="View all" onAction={onViewAll}>
          <div className="overview-routes">
            {visibleRoutes.length ? (
              visibleRoutes.map((route) => (
                <div className="overview-route" key={route.name}>
                  <span>{route.name}</span>
                  <div className="overview-route-track">
                    <div
                      style={{
                        width: `${(route.trips / maxRouteTrips) * 100}%`,
                      }}
                    />
                  </div>
                  <strong>{route.trips}</strong>
                </div>
              ))
            ) : (
              <div className="overview-empty">
                {t("No trips for the selected date range")}
              </div>
            )}
          </div>
          {metrics.routes.length > 6 && (
            <button
              type="button"
              className="overview-see-more"
              onClick={onViewAll}
            >
              {t("See more")}
            </button>
          )}
        </Panel>
        <Panel title="Vehicle Utilization">
          <div className="overview-utilization-donut overview-donut">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={
                    metrics.utilization.some((item) => item.value > 0)
                      ? metrics.utilization
                      : [{ name: "Empty", value: 1, color: "#e5e7eb" }]
                  }
                  dataKey="value"
                  innerRadius="70%"
                  outerRadius="94%"
                  startAngle={90}
                  endAngle={-270}
                  stroke="none"
                >
                  {(metrics.utilization.some((item) => item.value > 0)
                    ? metrics.utilization
                    : [{ name: "Empty", color: "#e5e7eb" }]
                  ).map((item) => (
                    <Cell key={item.name} fill={item.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="overview-donut-center">
              <strong>{metrics.fleetUtilization}%</strong>
              <small>{t("Utilized")}</small>
            </div>
          </div>
          <div className="overview-utilization-list">
            {metrics.utilization.map((item) => (
              <div key={item.name}>
                <span>
                  <i style={{ background: item.color }} />
                  {t(item.name)}
                </span>
                <span>
                  {item.value} ({item.percent}%)
                </span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Hourly Trip Distribution" info>
          <div className="overview-chart-short">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={metrics.hourly}
                margin={{ top: 12, right: 8, bottom: 0, left: -20 }}
              >
                <YAxis
                  domain={[0, Math.ceil(hourlyMax * 1.2) || 1]}
                  tick={{ fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <XAxis
                  dataKey="time"
                  interval={3}
                  tick={{ fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip />
                <Bar
                  dataKey="trips"
                  name={t("Trips")}
                  fill="#2f6bea"
                  maxBarSize={12}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </div>
  );
}
